import React, {memo, useCallback, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {scrollToElement} from '@/util/motion/smoothScroll';

interface TableOfContentsProps {
    items: string[];
    activeIndex: number | null;
    onItemClick: (index: number) => void;
}

/**
 * Table of Contents sidebar panel.
 *
 * Scroll highlighting is handled HERE with a requestAnimationFrame throttle,
 * so the parent page never registers an unthrottled scroll listener.
 *
 * The parent controls `activeIndex` state via `onItemClick` (for click-to-scroll)
 * and a passed-in setter from `useScrollHighlight` (for scroll-driven highlights).
 */
const TableOfContents = ({items, activeIndex, onItemClick}: TableOfContentsProps) => (
    <Box id="table_content">
        {items?.map((item, index) => (
            <div key={item || index}>
                <Typography className={activeIndex === index ? 'tabel-list active' : 'tabel-list'} onClick={() => onItemClick(index)}>
                    {item}
                </Typography>
            </div>
        ))}
    </Box>
);

export default memo(TableOfContents);

// ─── Scroll utility hook ─────────────────────────────────────────────────────

/**
 * Attaches a requestAnimationFrame-throttled scroll listener that updates
 * the active heading index based on h2 elements currently visible in the viewport.
 *
 * @param setActiveIndex - state setter from the parent page
 */
export function useScrollHighlight(setActiveIndex: (index: number) => void) {
    const rafId = useRef<number | null>(null);

    const highlight = useCallback(() => {
        const h2Tags = document.getElementsByTagName('h2');
        const pTags = document.querySelectorAll('#table_content p');

        for (let i = 0; i < h2Tags.length; i++) {
            const rect = h2Tags[i].getBoundingClientRect();
            const inViewport =
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (globalThis.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (globalThis.innerWidth || document.documentElement.clientWidth);

            if (inViewport) {
                setActiveIndex(i);
                pTags.forEach((p, j) => {
                    if (j === i) {
                        p.classList.add('active');
                    } else {
                        p.classList.remove('active');
                    }
                });
                break;
            }
        }
    }, [setActiveIndex]);

    const onScroll = useCallback(() => {
        if (rafId.current !== null) return; // already scheduled
        rafId.current = requestAnimationFrame(() => {
            highlight();
            rafId.current = null;
        });
    }, [highlight]);

    useEffect(() => {
        globalThis.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            globalThis.removeEventListener('scroll', onScroll);
            if (rafId.current !== null) cancelAnimationFrame(rafId.current);
        };
    }, [onScroll]);
}

// ─── Click-to-scroll utility ─────────────────────────────────────────────────

/**
 * Smoothly scrolls to the h2 at `tagIndex` inside `sectionId`,
 * accounting for the sticky navbar height.
 */
export function scrollToHeading(sectionId: string, tagIndex: number) {
    const section = document.getElementById(sectionId);
    const tags = section?.getElementsByTagName('h2');
    if (!tags || tagIndex < 0 || tagIndex >= tags.length) return;

    const navbarHeight = 170;
    scrollToElement(tags[tagIndex], navbarHeight);
}
